/**
 * UserPicker — Autocomplete searchable pour lier un contact à un user existant.
 *
 * Props :
 *   - value: string (id du user sélectionné) OU null
 *   - onChange(user | null) : callback avec le user complet {id, firstName, lastName, email, phone, role, companyName}
 *   - placeholder
 *   - includeClients (défaut true) — utilisateurs collection `users` (rôle client + expediteur)
 *   - includeInternal (défaut true) — utilisateurs collection `internal_users` (staff)
 *
 * Utilise `GET /api/unified-users/search-picker?q=...` (Fév 2026)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, X, User as UserIcon, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserPicker({
  value, onChange, placeholder = 'Rechercher par nom / email / téléphone / société...',
  includeClients = true, includeInternal = true,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);

  // Résout le user sélectionné depuis son ID (au montage)
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    axios.get(`${API}/unified-users/search-picker`, { params: { q: value, include_clients: includeClients, include_internal: includeInternal } })
      .then(({ data }) => {
        const u = (data?.items || []).find(x => x.id === value);
        if (u) setSelected(u);
      }).catch(() => {});
  }, [value, includeClients, includeInternal, selected?.id]);

  // Debounce search
  useEffect(() => {
    if (!open || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/unified-users/search-picker`, {
          params: { q: query, include_clients: includeClients, include_internal: includeInternal, limit: 15 },
        });
        setResults(data?.items || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, includeClients, includeInternal]);

  // Ferme le dropdown si clic à l'extérieur
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pick = useCallback((user) => {
    setSelected(user);
    onChange?.(user);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const clear = () => {
    setSelected(null);
    onChange?.(null);
    setQuery('');
  };

  const nameOf = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Sans nom';

  // Si un user est déjà sélectionné → afficher en mode "lié"
  if (selected) {
    return (
      <div className="mt-1 flex items-center gap-2 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2" data-testid="user-picker-linked">
        <UserIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-emerald-800 truncate">
            {nameOf(selected)}
            {selected.role && (
              <span className="ml-2 text-[10px] font-normal bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                {selected.role}
              </span>
            )}
          </div>
          <div className="text-xs text-emerald-600 truncate">
            {selected.email || '—'}{selected.phone ? ` · ${selected.phone}` : ''}
          </div>
        </div>
        <button type="button" onClick={clear}
                className="text-emerald-500 hover:text-rose-600 text-xs inline-flex items-center gap-1"
                data-testid="user-picker-unlink">
          <X className="h-3.5 w-3.5" /> Détacher
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 relative" ref={dropdownRef} data-testid="user-picker">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
        <input type="text" value={query}
               onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)}
               placeholder={placeholder}
               className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:border-blue-500 focus:outline-none"
               data-testid="user-picker-input" />
        {loading && (
          <Loader2 className="h-4 w-4 animate-spin absolute right-2.5 top-2.5 text-slate-400" />
        )}
      </div>
      {open && (query.length >= 2 || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto z-50" data-testid="user-picker-dropdown">
          {loading ? (
            <div className="p-3 text-center text-xs text-slate-400">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400">
              {query.length < 2 ? 'Tapez au moins 2 caractères' : 'Aucun utilisateur trouvé'}
            </div>
          ) : results.map(u => (
            <button type="button" key={u.id + u.source} onClick={() => pick(u)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0 text-sm"
                    data-testid={`user-picker-option-${u.id}`}>
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">
                    {nameOf(u)}
                    {u.role && (
                      <span className="ml-2 text-[10px] font-normal bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {u.role}
                      </span>
                    )}
                    {u.source === 'internal_users' && (
                      <span className="ml-1 text-[10px] font-normal bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        interne
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {u.email || '—'}{u.phone ? ` · ${u.phone}` : ''}
                    {u.companyName ? ` · ${u.companyName}` : ''}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
